import {
  CLASS_PROTOCOL_VERSION,
  type ClassClient,
  type ClassEvent,
  type ClassJoinOpts,
  type ClassMode,
  type ClassRoomState,
} from "./types";

/**
 * WebSocket transport → ClassRoom DO at /class/ws/<CODE>. Same shape as the
 * Quizzle transport (versioned hello, 1.5s heartbeat + resync, auto-reconnect).
 */
const HEARTBEAT_MS = 1500;

export class ClassTransport implements ClassClient {
  private ws: WebSocket | null = null;
  private url = "";
  private listeners: Array<(e: ClassEvent) => void> = [];
  private hello: ClassJoinOpts | null = null;
  private heartbeat: number | null = null;
  private reconnects = 0;

  async join(opts: ClassJoinOpts): Promise<ClassRoomState> {
    this.hello = opts;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.url = `${proto}://${location.host}/class/ws/${opts.code}`;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Class server didn't respond")), 6000);
      const off = this.on((e) => {
        if (e.type === "state") {
          const joined = opts.role === "teacher" || (opts.studentId ? e.state.onlineIds.includes(opts.studentId) : false);
          if (joined) { clearTimeout(timer); off(); resolve(e.state); }
        } else if (e.type === "error") {
          clearTimeout(timer); off(); reject(new Error(e.message));
        }
      });
      this.connect();
    });
  }

  private connect() {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.reconnects = 0;
      if (this.hello) {
        const h = this.hello;
        this.send(h.role === "teacher"
          ? { type: "hello", v: CLASS_PROTOCOL_VERSION, role: "teacher", hostId: h.hostId, className: h.className }
          : { type: "hello", v: CLASS_PROTOCOL_VERSION, role: "student", studentId: h.studentId, name: h.name, avatarConfig: h.avatarConfig });
      }
      this.startHeartbeat();
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "state") this.emit({ type: "state", state: msg.state });
        else if (msg.type === "error") this.emit({ type: "error", message: msg.message ?? "Class error" });
      } catch { /* ignore */ }
    };
    ws.onclose = () => {
      this.ws = null;
      this.stopHeartbeat();
      if (this.reconnects < 6 && this.hello) {
        this.reconnects++;
        setTimeout(() => this.connect(), Math.min(2000, 200 * 2 ** this.reconnects));
      }
    };
    ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeat = window.setInterval(() => this.send({ type: "ping", at: Date.now() }), HEARTBEAT_MS);
  }
  private stopHeartbeat() {
    if (this.heartbeat != null) { window.clearInterval(this.heartbeat); this.heartbeat = null; }
  }

  setMode(mode: ClassMode, activityCode?: string): void { this.send({ type: "set-mode", mode, activityCode }); }
  backToLobby(): void { this.send({ type: "back-to-lobby" }); }
  removeStudent(studentId: string): void { this.send({ type: "remove-student", studentId }); }

  on(cb: (e: ClassEvent) => void): () => void {
    this.listeners.push(cb);
    return () => { this.listeners = this.listeners.filter((l) => l !== cb); };
  }
  leave(): void {
    this.send({ type: "leave" });
    this.stopHeartbeat();
    this.hello = null;
    if (this.ws) { try { this.ws.close(); } catch { /* ignore */ } this.ws = null; }
    this.listeners = [];
  }

  private send(msg: unknown) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try { this.ws.send(JSON.stringify(msg)); } catch { /* reconnect handles it */ }
  }
  private emit(e: ClassEvent) { for (const l of this.listeners) l(e); }
}
